import { useState, useEffect } from 'react';
import api from '../../api/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from '../../config/utility';
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ThemeButton from "../../components/atoms/ThemeButton";
import SecondaryThemeButton from "../../components/atoms/SecondaryThemeButton";
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

const Register = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    referral_code: '',  // Added referral code field
  });

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [phoneError, setPhoneError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check for referral code in URL on component mount
  useEffect(() => {
    const referralCode = searchParams.get('ref');
    if (referralCode) {
      setForm(prev => ({ ...prev, referral_code: referralCode }));
    }
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Phone validation helper
  const validatePhone = (value) => {
    // Simple validation: at least 7 digits
    return value && value.replace(/\D/g, '').length >= 7;
  };

  const handlePhoneChange = (phone) => {
    setForm({ ...form, phone });
    setPhoneError(phone ? !validatePhone(phone) : false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/signup', form);
      navigate(routes.verify, { state: { email: form.email } });
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  return (
    <motion.div
      className="flex flex-col md:flex-row min-h-screen w-full overflow-x-hidden bg-body-auth"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Back Button */}
      <motion.button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 z-50 bg-white/90 hover:bg-white text-gray-700 hover:text-primary 
                   rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 
                   backdrop-blur-md border border-white/20"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ x: -50, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4" />
      </motion.button>

      {/* Left Image Panel - Hidden on mobile */}
      <motion.div
        className="hidden md:block md:w-1/2 h-screen bg-contain bg-no-repeat bg-center"
        style={{ backgroundImage: "url('/assets/register-illustration.png')" }}
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ type: "spring", duration: 0.8 }}
      />

      {/* Right Sign-up Panel */}
      <div className="w-full md:w-1/2 flex items-center justify-center min-h-screen pt-20 pb-28 md:py-8 px-4 sm:px-6 lg:px-8 relative backdrop-blur-lg">
        <motion.div
          className="w-full max-w-md sm:max-w-lg lg:max-w-3xl mx-auto bg-white/90 border border-white/20 rounded-2xl p-6 sm:p-8 pt-8 sm:pt-6 backdrop-blur-md shadow-xl z-20"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div
            className="text-start mb-6 sm:mb-8"
            variants={itemVariants}
          >
            <motion.img
              src="/assets/FULLLOGO.svg"
              alt="Popout"
              className="w-40 sm:w-48 md:w-64 mb-6 sm:mb-8"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", duration: 0.8 }}
            />
            <motion.h2
              className="text-xl sm:text-2xl font-display font-bold text-dark"
              variants={itemVariants}
            >
              Sign&nbsp;Up&nbsp;As&nbsp;Organiser
            </motion.h2>
          </motion.div>

          {/* Form */}
          <form onSubmit={handleRegister} className="space-y-4 sm:space-y-6">
            {/* Name Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs sm:text-sm mb-2 text-dark">Full Name</label>
              <input
                type="text"
                name="name"
                className="w-full"
                placeholder="John Doe"
                required
                value={form.name}
                onChange={handleChange}
              />
            </motion.div>

            {/* Email Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs sm:text-sm mb-2 text-dark">Email</label>
              <input
                type="email"
                name="email"
                className="w-full"
                placeholder="example@email.com"
                required
                value={form.email}
                onChange={handleChange}
              />
            </motion.div>

            {/* Password Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs sm:text-sm mb-2 text-dark">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="w-full pr-10"
                  placeholder="••••••••"
                  required
                  value={form.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-primary transition-colors z-10"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="w-4 h-4" />
                </button>
              </div>
            </motion.div>

            {/* Confirm Password Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs sm:text-sm mb-2 text-dark">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="password_confirmation"
                  className="w-full pr-10"
                  placeholder="••••••••"
                  required
                  value={form.password_confirmation}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-primary transition-colors z-10"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} className="w-4 h-4" />
                </button>
              </div>
            </motion.div>

            {/* Phone Input */}
            <motion.div variants={itemVariants}>
              <label className="block text-xs sm:text-sm mb-2 text-dark">Phone (Optional)</label>
              <div className="relative w-full">
                <ReactPhoneInput
                  country={"ae"}
                  value={form.phone}
                  onChange={handlePhoneChange}
                  preferredCountries={['ae', 'pk', 'in']}
                  enableSearch={true}
                  searchPlaceholder="Search country"
                  autoFormat={true}
                  inputStyle={{
                    width: "100%",
                    backgroundColor: "transparent",
                    border: phoneError ? "2px solid #EF4444" : "1px solid #D1D5DB",
                    borderRadius: "2rem",
                    padding: "0.75rem 1rem 0.75rem 3.5rem",
                    color: "#1F2937",
                    fontSize: "0.875rem",
                    height: "44px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s",
                    fontFamily: 'inherit',
                  }}
                  buttonStyle={{
                    border: "none",
                    backgroundColor: "transparent",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    position: "absolute",
                    height: "28px",
                    width: "36px",
                    padding: 0,
                    borderRadius: "1rem 0 0 1rem",
                  }}
                  inputProps={{
                    placeholder: "",
                    className: "placeholder:text-gray-400 focus:ring-2 focus:ring-primary focus:border-primary text-sm sm:text-base",
                  }}
                  dropdownClass="bg-white border border-gray-200 rounded-lg shadow-lg text-dark text-sm"
                  containerClass="w-full"
                />
                {phoneError && (
                  <span className="text-xs text-red-500 absolute left-0 -bottom-5">Please enter a valid phone number</span>
                )}
              </div>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              className="flex justify-center items-center w-full pt-2"
              variants={itemVariants}
            >
              <ThemeButton
                type="submit"
                label={loading ? "Please wait..." : "Sign Up"}
                onClick={handleRegister}
                disabled={loading}
                className="w-full sm:max-w-xs"
                primaryColor="bg-primary"
                secondaryColor="bg-accentSecondary"
                textColor="#ffffff"
                hoverTextColor="#160033"
              />
            </motion.div>
          </form>

          {/* Login Link */}
          <motion.p
            className="mt-6 sm:mt-8 text-xs sm:text-sm text-center text-grey"
            variants={itemVariants}
          >
            Already have an account?{" "}
            <SecondaryThemeButton
              label="Sign in"
              className="text-base sm:text-lg"
              onClick={() => navigate(routes.login)}
            />
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Register;